import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import API, { formatApiError } from "@/lib/api";
import { isInAppBrowser } from "@/lib/inAppBrowser";
import { startPayment } from "@/lib/razorpay";

const EMPTY_FORM = { name: "", age: "", mobile: "", email: "", state: "", city: "", pincode: "" };

export default function PurchaseModal({ session, onClose }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [paying, setPaying] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (session) {
      setForm(EMPTY_FORM);
      setErrors({});
      setSuccess(null);
    }
  }, [session]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (form.name.trim().length < 2) errs.name = "Please enter your full name";
    const age = parseInt(form.age, 10);
    if (!age || age < 1 || age > 120) errs.age = "Enter a valid age";
    if (!/^[0-9]{10}$/.test(form.mobile.trim())) errs.mobile = "Enter a valid 10-digit mobile number";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = "Enter a valid email address";
    if (form.state.trim().length < 2) errs.state = "Enter your state";
    if (form.city.trim().length < 2) errs.city = "Enter your city";
    if (!/^[0-9]{6}$/.test(form.pincode.trim())) errs.pincode = "Enter a valid 6-digit pin code";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleVerify = async (orderData, razorpayResponse) => {
    try {
      const { data } = await API.post("/purchase/verify", {
        registration_id: orderData.registration_id,
        razorpay_order_id: razorpayResponse.razorpay_order_id,
        razorpay_payment_id: razorpayResponse.razorpay_payment_id,
        razorpay_signature: razorpayResponse.razorpay_signature,
      });
      setSuccess({ emailSent: data.email_sent, email: form.email.trim() });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPaying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setPaying(true);
    try {
      const { data: orderData } = await API.post("/purchase/create-order", {
        session_id: session.id,
        name: form.name.trim(),
        age: parseInt(form.age, 10),
        mobile: form.mobile.trim(),
        email: form.email.trim(),
        state: form.state.trim(),
        city: form.city.trim(),
        pincode: form.pincode.trim(),
      });

      await startPayment({
        order: orderData,
        description: orderData.session_title,
        prefill: { name: form.name.trim(), email: form.email.trim(), contact: form.mobile.trim() },
        callbackPath: "/api/purchase/razorpay-callback",
        cancelPath: "/payment-status?type=workshop&status=failed",
        onSuccess: (resp) => handleVerify(orderData, resp),
        onDismiss: () => setPaying(false),
        onFailure: (resp) => {
          API.post("/purchase/failed", {
            registration_id: orderData.registration_id,
            reason: resp?.error?.description || "Payment failed",
          }).catch(() => {});
          setPaying(false);
          toast.error("Payment failed. Please try again.");
        },
      });
    } catch (e) {
      setPaying(false);
      const msg = formatApiError(e);
      if (msg === "payments_not_configured") {
        toast.error("Online payments are being set up. Please try again shortly.");
      } else {
        toast.error(msg);
      }
    }
  };

  const fields = [
    { key: "name", label: "Name", testid: "input-full-name", type: "text", placeholder: "Your full name" },
    { key: "age", label: "Age", testid: "input-age", type: "number", placeholder: "Your age" },
    { key: "mobile", label: "Mobile number", testid: "input-mobile", type: "tel", placeholder: "10-digit mobile number" },
    { key: "email", label: "Email Id", testid: "input-email", type: "email", placeholder: "you@example.com" },
    { key: "state", label: "State", testid: "select-state", type: "text", placeholder: "e.g. Assam" },
    { key: "city", label: "City", testid: "input-city", type: "text", placeholder: "e.g. Guwahati" },
    { key: "pincode", label: "Pin code", testid: "input-pincode", type: "text", placeholder: "6-digit pin code" },
  ];

  return (
    <Dialog open={!!session} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md bg-[#FDFBF7] rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="purchase-modal">
        {success ? (
          <div className="text-center py-6" data-testid="purchase-success">
            <CheckCircle2 className="mx-auto text-[#2D5A47]" size={52} />
            <h3 className="font-display text-2xl font-bold text-[#2D5A47] mt-4">
              Congratulations, {form.name.split(" ")[0]}!
            </h3>
            <p className="text-sm text-[#5A6E63] mt-2 leading-relaxed">
              You have successfully registered for <strong>{session?.title}</strong>.
              {success.emailSent
                ? ` Your receipt and workshop link have been emailed to ${success.email}.`
                : " Your receipt will reach your email shortly."}
            </p>
            <p className="text-xs text-[#5A6E63] mt-3">
              You can view your session details, Zoom link and WhatsApp group anytime in your dashboard.
            </p>
            <Link to="/dashboard" data-testid="go-to-dashboard-btn">
              <Button className="mt-6 rounded-full bg-[#2D5A47] hover:bg-[#224536] text-[#FDFBF7] px-8">
                Go to My Dashboard
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl text-[#2D5A47]">{session?.title}</DialogTitle>
              <DialogDescription className="text-[#5A6E63]">
                {session?.date} at {session?.time} &middot;{" "}
                <span className="font-semibold text-[#1E2A24]">&#8377;{session?.price}</span>
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3 mt-2" data-testid="purchase-form">
              {fields.map(({ key, label, testid, type, placeholder }) => (
                <div key={key}>
                  <Label htmlFor={testid} className="text-xs text-[#5A6E63]">{label}</Label>
                  <Input
                    id={testid}
                    data-testid={testid}
                    type={type}
                    value={form[key]}
                    onChange={set(key)}
                    placeholder={placeholder}
                    className="mt-1 bg-[#FAF6F0] border-[#2D5A47]/20 focus-visible:ring-[#2D5A47]"
                  />
                  {errors[key] && (
                    <p data-testid={`error-${key}`} className="text-xs text-red-600 mt-1">{errors[key]}</p>
                  )}
                </div>
              ))}
              {isInAppBrowser() && (
                <div data-testid="inapp-payment-warning" className="bg-[#F9EBEA] border border-[#96690F]/30 rounded-xl p-3 text-xs text-[#96690F] leading-relaxed">
                  You will be taken to Razorpay&apos;s secure payment page. Pay with <strong>UPI ID, Card or Netbanking</strong> - you will be brought back here automatically after payment.
                </div>
              )}
              <Button
                type="submit"
                data-testid="complete-payment-button"
                disabled={paying}
                className="w-full rounded-full bg-[#D98E32] hover:bg-[#c17a24] text-[#FDFBF7] h-11 mt-2"
              >
                {paying ? (
                  <span className="flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Processing...</span>
                ) : (
                  <>Get it &middot; Pay &#8377;{session?.price}</>
                )}
              </Button>
              <p className="text-[11px] text-center text-[#5A6E63]">
                Secure payment via Razorpay. Receipt and workshop link will be emailed to you.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
